import React, { useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form as FormikForm, FormikProps } from 'formik';
import { InferType, object, string, number } from 'yup';

import { FormGroupControl } from '@components/Base';
import { ServerError } from '@components/Helper';
import i18n from '@components/i18n';
import { HealthClaim } from '@models/HealthClaim';
import { mergeInitialValues } from '@utils/formik';
import { DATETIME_FORMAT, formatDateTime } from '@utils/datetime';

import { healthClaimUpdate, HealthClaimUpdatePayload } from './action';
import { selectHealthClaim } from './selector';

export interface HealthClaimDetailsEditModalProps {
  isOpen: boolean;
  healthClaim: HealthClaim;
  onClose: () => void;
  onSuccess?: () => void;
}

const groupProps = { className: 'mb-3' };

const validationSchema = object({
  accidentHappenedAt: string().default('').required(i18n.t('validation:required')),
  dischargedAt: string().default('').required(i18n.t('validation:required')),
  hospitalLengthOfStay: number().default(0).required(i18n.t('validation:required')),
  symptomsDescription: string().default('').required(i18n.t('validation:required')),
  diagnosisDescription: string().default('').required(i18n.t('validation:required')),
  hospitalName: string().default('').required(i18n.t('validation:required')),
});

type FormValues = InferType<typeof validationSchema>;

const HealthClaimDetailsEditModal = ({ isOpen, healthClaim, onClose, onSuccess }: HealthClaimDetailsEditModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const { isUpdatingHealthClaim, serverErrors } = useSelector(selectHealthClaim);
  const {
    id,
    claimNumber,
    accidentHappenedAt,
    dischargedAt,
    hospitalLengthOfStay,
    symptomsDescription,
    diagnosisDescription,
    hospitalName,
  } = healthClaim;

  const initialValues: FormValues = mergeInitialValues(validationSchema.getDefault(), {
    accidentHappenedAt: formatDateTime(accidentHappenedAt, DATETIME_FORMAT.INPUT),
    dischargedAt: formatDateTime(dischargedAt, DATETIME_FORMAT.INPUT),
    hospitalLengthOfStay,
    symptomsDescription,
    diagnosisDescription,
    hospitalName,
  });

  const handleSubmit = (values: FormValues) => {
    const payload: HealthClaimUpdatePayload = {
      id: id,
      ...values,
    };
    dispatch(healthClaimUpdate(payload));
  };

  useEffect(() => {
    if (formikRef.current?.isSubmitting && !isUpdatingHealthClaim && isEmpty(serverErrors)) {
      formikRef.current?.setSubmitting(false);
      onSuccess && onSuccess();
    }

    if (formikRef.current?.isSubmitting && !isUpdatingHealthClaim && !isEmpty(serverErrors)) {
      formikRef.current?.setSubmitting(false);
    }
  }, [isUpdatingHealthClaim, serverErrors, onSuccess, formikRef]);

  return (
    <Modal size="lg" show={isOpen} onHide={onClose} centered>
      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, isSubmitting }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title>{t('claimEditModal.title', { claimNumber })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormGroupControl
                type="datetime-local"
                name="accidentHappenedAt"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.accidentHappenedAt.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                type="datetime-local"
                name="dischargedAt"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.dischargeDate.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                name="hospitalLengthOfStay"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.hospitalLengthOfStay.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                as="textarea"
                name="symptomsDescription"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.symptomsDescription.label')}
                groupProps={groupProps}
                required
              />
              <FormGroupControl
                as="textarea"
                name="diagnosisDescription"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.diagnosisDescription.label')}
                groupProps={groupProps}
                required
              />
              <FormGroupControl
                name="hospitalName"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.hospitalName.label')}
                required
                groupProps={groupProps}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? t('common:saving') : t('common:save')}
              </Button>
            </Modal.Footer>

            <ServerError errors={serverErrors} />
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export { HealthClaimDetailsEditModal };
